import React from 'react';
import { Star, ShoppingBag, Award, Tag, Wine } from 'lucide-react';
import type { Product, TileSize } from '../utils/types';
import { WINE_TYPE_META } from '../utils/types';
import { useI18n } from '../i18n/I18nContext';
import { useFontSizes } from '../contexts/FontSizeContext';
import { safeUrl, openInWorkingTab } from '../utils/navigate';
import type { TranslationKey } from '../i18n/index';

interface ProductCardProps {
  product: Product;
  compact?: boolean;
  tileSize?: TileSize;
  onProductClick?: (product: Product) => void;
}

const TILE_SIZE_CONFIG = {
  small:  { imageH: '120px', padding: 'p-2',   titleClass: 'text-xs',   showRating: false, showStock: true,  showSavings: false },
  medium: { imageH: '160px', padding: 'p-2.5', titleClass: 'text-sm',   showRating: false, showStock: true,  showSavings: false },
  large:  { imageH: '200px', padding: 'p-3',   titleClass: '',          showRating: true,  showStock: true,  showSavings: true  },
} as const;

/**
 * Renders a star rating display.
 */
function StarRating({ rating, count }: { rating: number; count?: number }) {
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

  return (
    <div className="botilia-stars items-center" aria-label={`${rating} / 5`}>
      {Array.from({ length: fullStars }).map((_, i) => (
        <Star
          key={`full-${i}`}
          className="h-3.5 w-3.5 fill-gold text-gold"
        />
      ))}
      {hasHalf && (
        <div className="relative h-3.5 w-3.5">
          <Star className="absolute h-3.5 w-3.5 text-gray-300" />
          <div className="absolute overflow-hidden w-1/2">
            <Star className="h-3.5 w-3.5 fill-gold text-gold" />
          </div>
        </div>
      )}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <Star
          key={`empty-${i}`}
          className="h-3.5 w-3.5 text-gray-300"
        />
      ))}
      {count !== undefined && count > 0 && (
        <span className="ml-1 text-xs text-gray-400">({count})</span>
      )}
    </div>
  );
}

/**
 * Formats price in Euros with locale based on language.
 */
function formatPrice(price: number, locale = 'el-GR'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(price);
}

export const ProductCard = React.memo<ProductCardProps>(function ProductCard({
  product,
  compact = false,
  tileSize,
  onProductClick,
}) {
  const { t, lang } = useI18n();
  const { body, small } = useFontSizes();
  const locale = lang === 'en' ? 'en-IE' : 'el-GR';
  const displayName = (lang === 'el' && product.nameEl) ? product.nameEl : product.name;

  // tileSize takes precedence over compact boolean
  const size = tileSize ?? (compact ? 'medium' : 'large');
  const cfg = TILE_SIZE_CONFIG[size];
  const wineTypeMeta = product.wineType
    ? WINE_TYPE_META[product.wineType]
    : null;

  const hasDiscount =
    product.salePrice !== undefined && product.salePrice < product.price;
  const discountPercent = hasDiscount
    ? Math.round(
        ((product.price - product.salePrice!) / product.price) * 100,
      )
    : 0;
  const savingsAmount = hasDiscount
    ? product.price - product.salePrice!
    : 0;

  // Availability is driven by the feed's authoritative purchasability flag
  // (mapped to inStock from `canBeBought`): some buyable items are priced 0
  // (e.g. accessories priced at checkout), so don't second-guess it by price.
  // The price block is hidden separately when effectivePrice <= 0.
  const effectivePrice = hasDiscount ? product.salePrice! : product.price;
  const isAvailable = product.inStock;
  // Promoted products can reach the card without passing through the search
  // index, which is where the shop's "-" placeholder is normally dropped.
  const producer = product.producer?.trim() === '-' ? undefined : product.producer;

  return (
    <a
      href={safeUrl(product.url)}
      onClick={(e) => { onProductClick?.(product); openInWorkingTab(product.url, e); }}
      className={[
        'botilia-card group block overflow-hidden text-center',
        'focus:outline-none focus:ring-2 focus:ring-brand/30 focus:ring-offset-2',
        'transition-all duration-300',
        compact ? 'w-[180px]' : 'w-full',
      ].join(' ')}
      aria-label={`${displayName} - ${formatPrice(hasDiscount ? product.salePrice! : product.price, locale)}`}
    >
      {/* Image */}
      <div
        className="relative bg-white overflow-hidden flex items-center justify-center"
        style={{ height: cfg.imageH }}
      >
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={displayName}
            loading="lazy"
            className="w-full h-full object-contain p-3 transition-transform duration-300 group-hover:scale-110"
            // Inline styles defeat any host-page CSS that overrides object-fit
            // (some sites apply `img { object-fit: cover }` globally).
            style={{
              objectFit: 'contain',
              objectPosition: 'center',
              width: '100%',
              height: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
            }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        {/* Fallback icon */}
        <div
          className={[
            'flex items-center justify-center text-gray-300',
            product.imageUrl ? 'hidden' : '',
          ].join(' ')}
        >
          <Wine className="h-16 w-16" />
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300 pointer-events-none" />

        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {product.isBestSeller && (
            <span className="botilia-badge botilia-badge-bestseller shadow-sm">
              <Award className="h-3 w-3 mr-1" />
              {t('product.bestSeller')}
            </span>
          )}
          {product.isPromo && (
            <span className="botilia-badge botilia-badge-promo shadow-sm animate-pulse-subtle">
              <Tag className="h-3 w-3 mr-1" />
              {t('product.promo')}
            </span>
          )}
          {hasDiscount && (
            <span className="botilia-badge bg-red-600 text-white shadow-sm">
              -{discountPercent}%
            </span>
          )}
        </div>

        {/* Stock indicator */}
        {!isAvailable && (
          <div className="absolute bottom-2 right-2">
            <span className="botilia-badge bg-gray-800/90 text-white text-[10px]">
              {t('product.outOfStock')}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className={cfg.padding}>
        {/* Wine type badge */}
        {wineTypeMeta && (
          <span
            className={[
              'inline-block text-[10px] font-semibold uppercase tracking-wider mb-1 px-1.5 py-0.5 rounded',
              wineTypeMeta.bgColor,
              wineTypeMeta.color,
            ].join(' ')}
          >
            {t(wineTypeMeta.labelKey as TranslationKey)}
          </span>
        )}

        {/* Name */}
        <h3
          className={[
            'font-semibold text-gray-900 leading-tight line-clamp-2 group-hover:text-brand-ink',
            cfg.titleClass || body,
          ].join(' ')}
        >
          {displayName}
        </h3>

        {/* Producer / Region */}
        {(producer || product.region) && (
          <p
            className={[
              'text-gray-500 mt-0.5 truncate',
              size === 'small' ? 'text-[10px]' : small,
            ].join(' ')}
          >
            {producer}
            {producer && product.region ? ' · ' : ''}
            {product.region}
          </p>
        )}

        {/* Rating */}
        {product.rating !== undefined && product.rating > 0 && cfg.showRating && (
          <div className="mt-1.5">
            <StarRating
              rating={product.rating}
              count={product.reviewCount}
            />
          </div>
        )}

        {/* Price — hidden when there's no real price (e.g. gift packs priced 0) */}
        {(hasDiscount ? product.salePrice! : product.price) > 0 && (
          <div className="mt-2">
            <div className="flex items-baseline justify-center gap-2">
              {hasDiscount ? (
                <>
                  <span className="botilia-price-sale text-lg">
                    {formatPrice(product.salePrice!, locale)}
                  </span>
                  <span className="botilia-price-original">
                    {formatPrice(product.price, locale)}
                  </span>
                </>
              ) : (
                <span className="text-lg font-bold text-gray-900">
                  {formatPrice(product.price, locale)}
                </span>
              )}
            </div>
            {hasDiscount && cfg.showSavings && (
              <p className="text-xs font-semibold text-green-600 mt-0.5">
                {t('product.savings', { amount: formatPrice(savingsAmount, locale) })}
              </p>
            )}
          </div>
        )}

        {/* Stock indicator */}
        {cfg.showStock && (
          <div className={`mt-2 flex items-center justify-center gap-1.5 text-xs font-medium ${
            isAvailable ? 'text-green-700' : 'text-gray-500'
          }`}>
            <span className={`w-2 h-2 rounded-full ${
              isAvailable ? 'bg-green-500' : 'bg-gray-400'
            }`} />
            {isAvailable ? (
              <>
                <ShoppingBag className="h-3 w-3" />
                <span>{t('product.inStock')}</span>
              </>
            ) : (
              <span>{t('product.notAvailable')}</span>
            )}
          </div>
        )}
      </div>
    </a>
  );
});
